import { useState, useMemo } from "react";
import {
  MagnifyingGlass,
  BookOpen,
  CursorText,
  Copy,
  HashStraight,
  CaretDown,
  Trash,
} from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "../../../lib/cn";
import { CITATION_STYLES, formatCitation, formatInlineRef, type CitationStyle } from "../../../lib/citationFormat";
import type { BibEntry } from "../../../stores/citation";

const MOCK_CITATIONS: BibEntry[] = [
  {
    type: "article",
    key: "knuth1984",
    title: "Literate Programming",
    authors: ["Donald Knuth"],
    year: "1984",
    journal: "The Computer Journal",
    doi: "10.1093/comjnl/27.2.97",
    url: null,
    abstract: null,
  },
  {
    type: "book",
    key: "lamport1994",
    title: "LaTeX: A Document Preparation System",
    authors: ["Leslie Lamport"],
    year: "1994",
    journal: null,
    doi: null,
    url: null,
    abstract: null,
  },
];

const MOCK_DOC_KEYS = ["vaswani2017", "brown2020"];

export function DemoCitationsPanel({ onSignIn }: { onSignIn: () => void }) {
  const [selectedStyle, setSelectedStyle] = useState<CitationStyle>("apa");
  const [expandedKey, setExpandedKey] = useState<string | null>(null);

  return (
    <div className="flex h-full flex-col">
      <div className="shrink-0 px-3 py-2">
        <div className="flex items-center justify-between">
          <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
            Citations
          </span>
        </div>
        {/* Style pill toggle (demo) */}
        <div className="flex items-center gap-0.5 mt-1.5 rounded-[var(--radius-sm)] border border-[var(--border-hairline)] bg-[var(--bg-base)] p-0.5">
          {CITATION_STYLES.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelectedStyle(s.id)}
              className={cn(
                "relative flex-1 rounded-[3px] px-1.5 py-0.5 text-[9px] font-medium text-center",
                "transition-colors duration-75",
                selectedStyle === s.id
                  ? "text-[var(--text-primary)]"
                  : "text-[var(--text-muted)] hover:text-[var(--text-secondary)]",
              )}
            >
              {selectedStyle === s.id && (
                <motion.div
                  layoutId="demo-cite-style-pill"
                  className="absolute inset-0 rounded-[3px] bg-[var(--bg-surface)] shadow-sm border border-[var(--border-hairline)]"
                  transition={{ type: "spring", stiffness: 500, damping: 35, mass: 0.6 }}
                />
              )}
              <span className="relative z-[1]">{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Search */}
      <div className="shrink-0 px-3 pb-2">
        <div className="relative">
          <MagnifyingGlass
            size={12}
            className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--text-muted)]"
          />
          <input
            type="text"
            placeholder="Search papers..."
            onClick={onSignIn}
            readOnly
            className={cn(
              "w-full rounded-[var(--radius-md)] border border-[var(--border-hairline)]",
              "bg-[var(--bg-base)] pl-7 pr-3 py-1.5 text-[11px] text-[var(--text-primary)]",
              "placeholder:text-[var(--text-muted)] cursor-pointer",
              "hover:border-[var(--border-default)] transition-colors duration-100",
            )}
          />
        </div>
      </div>

      <div className="flex-1 overflow-y-auto px-2">
        {/* In Document section */}
        <div className="mt-1 mb-1">
          <div className="px-1 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              In Document
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            {MOCK_DOC_KEYS.map((key) => (
              <div
                key={key}
                className={cn(
                  "flex items-center justify-between gap-2 rounded-[var(--radius-sm)] px-2 py-1.5",
                  "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
                )}
              >
                <div className="flex items-center gap-1.5 min-w-0">
                  <HashStraight size={10} className="shrink-0 text-[var(--text-muted)]" />
                  <span className="text-[10px] font-mono text-[var(--text-secondary)] truncate">
                    {key}
                  </span>
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                  <div
                    className={cn(
                      "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
                      "text-[var(--text-muted)] hover:text-[var(--color-lever-orange)]",
                      "hover:bg-[var(--color-lever-orange)]/10 transition-colors duration-75 cursor-pointer",
                    )}
                    title="Insert at cursor"
                    onClick={onSignIn}
                  >
                    <CursorText size={11} weight="bold" />
                  </div>
                  <div
                    className={cn(
                      "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
                      "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
                      "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75 cursor-pointer",
                    )}
                    title="Copy reference"
                    onClick={onSignIn}
                  >
                    <Copy size={11} />
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* My Citations section */}
        <div className="mt-1 mb-1">
          <div className="px-1 py-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-[var(--text-muted)]">
              My Citations
            </span>
          </div>
          <div className="flex flex-col gap-0.5">
            {MOCK_CITATIONS.map((c, idx) => (
              <DemoBibEntryRow
                key={c.key}
                entry={c}
                index={idx}
                citationStyle={selectedStyle}
                isExpanded={expandedKey === c.key}
                onToggle={() => setExpandedKey(expandedKey === c.key ? null : c.key)}
                onClick={onSignIn}
              />
            ))}
          </div>
        </div>

        {/* Empty state hint */}
        <div className="flex flex-col items-center gap-2 py-4 px-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-[var(--bg-surface-hover)] border border-[var(--border-hairline)]">
            <BookOpen size={20} weight="duotone" className="text-[var(--text-muted)]" />
          </div>
          <div className="text-center">
            <p className="text-[11px] text-[var(--text-muted)]">
              Search for papers to cite
            </p>
            <p className="mt-0.5 text-[10px] text-[var(--text-muted)] opacity-60">
              Powered by Semantic Scholar
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function DemoBibEntryRow({
  entry,
  index,
  citationStyle,
  isExpanded,
  onToggle,
  onClick,
}: {
  entry: BibEntry;
  index: number;
  citationStyle: CitationStyle;
  isExpanded: boolean;
  onToggle: () => void;
  onClick: () => void;
}) {
  const formatted = useMemo(
    () => formatCitation(entry, citationStyle),
    [entry, citationStyle],
  );
  const inlineRef = useMemo(
    () => formatInlineRef(entry, citationStyle, index),
    [entry, citationStyle, index],
  );

  return (
    <div
      className={cn(
        "rounded-[var(--radius-sm)] px-2 py-1.5",
        "bg-[var(--bg-surface)] border border-[var(--border-hairline)]",
      )}
    >
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0 flex-1">
          <span className="text-[10px] font-mono text-[var(--text-secondary)] truncate block">
            {entry.key}
          </span>
          <span className="text-[9px] text-[var(--text-muted)] truncate block mt-0.5">
            {inlineRef}
          </span>
        </div>
        <div className="flex items-center gap-0.5 shrink-0">
          <div
            className={cn(
              "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--color-lever-orange)]",
              "hover:bg-[var(--color-lever-orange)]/10 transition-colors duration-75 cursor-pointer",
            )}
            title="Insert at cursor"
            onClick={onClick}
          >
            <CursorText size={11} weight="bold" />
          </div>
          <div
            className={cn(
              "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--text-primary)]",
              "hover:bg-[var(--bg-surface-hover)] transition-colors duration-75 cursor-pointer",
            )}
            title="Copy reference"
            onClick={onClick}
          >
            <Copy size={11} />
          </div>
          <div
            className={cn(
              "flex items-center justify-center w-5 h-5 rounded-[var(--radius-sm)]",
              "text-[var(--text-muted)] hover:text-[var(--color-soft-red)]",
              "hover:bg-[var(--color-soft-red)]/10 transition-colors duration-75 cursor-pointer",
            )}
            title="Remove"
            onClick={onClick}
          >
            <Trash size={11} weight="bold" />
          </div>
        </div>
      </div>
      {/* Preview toggle */}
      <button
        onClick={onToggle}
        className={cn(
          "mt-1 flex items-center gap-0.5 text-[9px] text-[var(--text-muted)]",
          "hover:text-[var(--text-secondary)] transition-colors duration-75",
        )}
      >
        <motion.span
          animate={{ rotate: isExpanded ? 0 : -90 }}
          transition={{ duration: 0.12 }}
          className="inline-flex"
        >
          <CaretDown size={8} weight="bold" />
        </motion.span>
        Preview
      </button>
      <AnimatePresence>
        {isExpanded && (
          <motion.p
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.15 }}
            className="mt-1 text-[9px] leading-relaxed text-[var(--text-secondary)] overflow-hidden italic"
          >
            {formatted}
          </motion.p>
        )}
      </AnimatePresence>
    </div>
  );
}
